/**
 * SuiteReportsView
 *
 * Renders a single ClickHouse report suite (all of its panels) for the
 * chrome-less Direct Reports deep-link page. It reuses the existing Infralytiqs
 * panel components (`ReportPanel`, `CounterTile`, `RealtimePanel`) so the output
 * matches `/#/infralytiqs/reports` exactly.
 *
 * Unlike the routed Reports page, drill-down here is handled IN-PAGE via local
 * state (there is no nav chrome to deep-link against). Behaviour is read from
 * the suite DSL so shareable / Direct Reports match `/#/infralytiqs/reports`:
 *   - Level 1: a panel/counter `onPanelClick` opens its drill-down report.
 *   - Level 2: a nested panel click (env counter → users table) or a row
 *     `onRowClick` stacks `level2ReportNames` (or the single target report)
 *     filtered by `passField = clicked value`.
 *   - Level 2 (user_id): per-user page, same as UserDrillDownPage.
 * Panel ingest-API (API Specs) buttons are omitted — this view is for shared
 * credentials links, SSO Direct Reports, and the Infralytiqs SDK embed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Typography, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { reportsDrilldownBackButtonSx } from '../Reports/reportsToolbarSurface';
import apiClient from 'utils/ApiClient';
import { ReportPanel } from '../Reports/ReportPanel';
import { CounterTile } from '../Reports/CounterTile';
import { RealtimePanel } from '../Reports/RealtimePanel';
import {
  FilterDef, ReportDefinition, ReportSuiteDetail, TimeRangeDef,
  sortReportsByLayout,
} from '../Settings/reportDsl';
import { resolveLinkedL2Reports, resolveLinkedPanelReports } from '../Reports/resolveLinkedL2Reports';

interface SuiteReportsViewProps {
  configId: string;
  tenantId: string;
  siteId: string;
  suiteId: string;
  timeRangeOverride: TimeRangeDef;
  /** Bumped by the toolbar Refresh button to force a suite re-fetch. */
  refreshKey: number;
}

const clampSpan = (w?: number): number => Math.min(Math.max(w ?? 12, 3), 12);

export const SuiteReportsView: React.FC<SuiteReportsViewProps> = ({
  configId,
  tenantId,
  siteId,
  suiteId,
  timeRangeOverride,
  refreshKey,
}) => {
  const theme = useTheme();
  const [suiteDetail, setSuiteDetail] = useState<ReportSuiteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // In-page drill-down state.
  const [level1ReportId, setLevel1ReportId] = useState<string | null>(null);
  const [userDrill, setUserDrill] = useState<{ reportId: string; userId: string } | null>(null);
  // Modal drill "stack" for non-user_id row clicks (e.g. search term → events →
  // single-event detail). Each frame is narrowed by the runtime filters of
  // every level above it.
  const [drillStack, setDrillStack] = useState<
    Array<{ report: ReportDefinition; runtimeFilters: FilterDef[]; title: string }>
  >([]);
  /** In-page L2 stack (same as Reports.tsx `linkedL2Reports`), not a modal. */
  const [l2View, setL2View] = useState<{
    reports: ReportDefinition[];
    runtimeFilters: FilterDef[];
    badge?: string;
  } | null>(null);

  // Reset any open drill-down when the selected suite changes.
  useEffect(() => {
    setLevel1ReportId(null);
    setUserDrill(null);
    setDrillStack([]);
    setL2View(null);
  }, [configId, suiteId]);

  const fetchSuiteDetail = useCallback(async () => {
    if (!configId || !suiteId) {
      setSuiteDetail(null);
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    try {
      const res: any = await apiClient.get(
        `/il/settings/module-config/${configId}/report-suites/${suiteId}`,
      );
      setSuiteDetail(res?.data ?? null);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to load report suite');
    } finally {
      setLoading(false);
    }
  }, [configId, suiteId]);

  useEffect(() => { void fetchSuiteDetail(); }, [fetchSuiteDetail, refreshKey]);

  const allReports = suiteDetail?.reports ?? [];

  const isToolbar = (r: ReportDefinition) => r.mode === 'toolbar';
  const isRealtime = (r: ReportDefinition) => {
    const t = r.visualizations?.[0]?.type;
    return t === 'realtime' || t === 'realtime_bar';
  };

  const drilldownReports = useMemo(
    () => allReports.filter((r) => r.mode === 'drilldown'),
    [allReports],
  );
  const toolbarReports = useMemo(
    () => sortReportsByLayout(allReports.filter((r) => isToolbar(r) && !isRealtime(r))),
    [allReports],
  );
  const realtimeReports = useMemo(
    () => sortReportsByLayout(
      allReports.filter((r) => r.mode !== 'drilldown' && !isToolbar(r) && isRealtime(r)),
    ),
    [allReports],
  );
  const dashboardReports = useMemo(
    () => sortReportsByLayout(
      allReports.filter((r) => r.mode !== 'drilldown' && !isToolbar(r) && !isRealtime(r)),
    ),
    [allReports],
  );

  // Level-2 (per-user) panels: every drill-down report except the "user list"
  // navigators (those whose row-click drills down by user_id) — on a page
  // already scoped to a single user they would just show that one user.
  // When the navigator report declares a curated `level2ReportNames` set, the
  // per-user view is restricted to exactly those reports, in that order.
  const userDrilldownReports = useMemo(
    () => {
      const all = sortReportsByLayout(
        drilldownReports.filter((r) => r.interactions?.onRowClick?.passField !== 'user_id'),
      );
      const navigator = userDrill?.reportId
        ? allReports.find((r) => r._id === userDrill.reportId)
        : undefined;
      const wanted = navigator?.interactions?.onRowClick?.level2ReportNames;
      if (wanted && wanted.length > 0) {
        return wanted
          .map((name) => all.find((r) => r.name === name))
          .filter((r): r is NonNullable<typeof r> => Boolean(r));
      }
      return all;
    },
    [drilldownReports, allReports, userDrill],
  );

  /** Panel/counter click → open the configured drill-down report. */
  const handlePanelClick = useCallback((sourceReport: ReportDefinition) => {
    const cfg = sourceReport.interactions?.onPanelClick;
    if (!cfg) return;
    const target = cfg.targetReportId
      ? drilldownReports.find((r) => r._id === cfg.targetReportId)
      : (cfg.targetReportName
        ? drilldownReports.find((r) => r.name === cfg.targetReportName)
        : undefined);
    if (!target?._id) return;
    // Already on the env-tile page: nested click opens the users table.
    // Keep `level1ReportId` so Back from L2 returns to the env counters.
    if (level1ReportId) {
      setL2View({ reports: [target], runtimeFilters: [], badge: undefined });
      return;
    }
    setLevel1ReportId(target._id);
  }, [drilldownReports, level1ReportId]);

  /** Row click — same DSL as Reports.tsx (`onRowClick` + `level2ReportNames`). */
  const handleRowClick = useCallback((row: Record<string, any>, sourceReport: ReportDefinition) => {
    const cfg = sourceReport.interactions?.onRowClick;
    if (!cfg?.passField) return;
    const dims = sourceReport.query.dimensions || [];
    const dimIdx = dims.findIndex((d) => d.field === cfg.passField);
    const metrics = sourceReport.query.metrics || [];
    const metIdx = metrics.findIndex((m) => m.field === cfg.passField);
    const value = dimIdx >= 0
      ? (row[`dim_${dimIdx}`] ?? row[cfg.passField])
      : (metIdx >= 0 ? row[`metric_${metIdx}`] : row[cfg.passField]);
    if (value === undefined || value === null || value === '') return;

    if (cfg.passField === 'user_id' && drillStack.length === 0) {
      setUserDrill({ reportId: sourceReport._id || '', userId: String(value) });
      return;
    }

    const newFilter: FilterDef = { field: cfg.passField, op: 'eq', value: String(value) };

    // First non-user_id row click → in-page stacked L2 panels from the suite DSL.
    if (!l2View) {
      const reports = resolveLinkedL2Reports(sourceReport, allReports, drilldownReports);
      if (reports.length > 0) {
        const label = dims.find((d) => d.field === cfg.passField)?.label || cfg.passField;
        setL2View({
          reports,
          runtimeFilters: [newFilter],
          badge: `${label} = ${value}`,
        });
        return;
      }
    }

    // Nested drill from an L2 panel (search term → events → event detail).
    const target = cfg.targetReportId
      ? drilldownReports.find((r) => r._id === cfg.targetReportId)
      : (cfg.targetReportName
        ? drilldownReports.find((r) => r.name === cfg.targetReportName)
        : undefined);
    if (!target) return;
    setDrillStack((prev) => {
      const parentFilters = prev.length ? prev[prev.length - 1].runtimeFilters : [];
      const inherited = parentFilters.length ? parentFilters : (l2View?.runtimeFilters || []);
      const title = target.visualizations?.[0]?.title || target.name;
      return [...prev, { report: target, runtimeFilters: [...inherited, newFilter], title }];
    });
  }, [allReports, drilldownReports, drillStack.length, l2View]);

  const level1Report = useMemo(
    () => (level1ReportId ? drilldownReports.find((r) => r._id === level1ReportId) : undefined),
    [level1ReportId, drilldownReports],
  );

  const level1LinkedReports = useMemo(() => {
    if (!level1Report) return [];
    const source = allReports.find((r) =>
      r.interactions?.onPanelClick?.targetReportId === level1Report._id
      || r.interactions?.onPanelClick?.targetReportName === level1Report.name,
    );
    if (source) {
      const linked = resolveLinkedPanelReports(source, allReports, drilldownReports);
      if (linked.length > 0) return linked;
    }
    return [level1Report];
  }, [level1Report, allReports, drilldownReports]);

  const userRuntimeFilters: FilterDef[] = useMemo(
    () => (userDrill ? [{ field: 'user_id', op: 'eq', value: userDrill.userId }] : []),
    [userDrill],
  );

  // Shared multi-level row-click drill modal — rendered alongside every view
  // so a row click from the dashboard or a level-1 drill-down can open it.
  const detailDialogNode = (
    <Dialog
      open={drillStack.length > 0}
      onClose={() => setDrillStack([])}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        {drillStack.length > 1 && (
          <IconButton aria-label="Back" size="small" color="inherit" onClick={() => setDrillStack((s) => s.slice(0, -1))}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {drillStack[drillStack.length - 1]?.title || 'Details'}
        </Box>
        <IconButton aria-label="Close" color="inherit" onClick={() => setDrillStack([])} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {drillStack.length > 0 && (
          <ReportPanel
            key={drillStack.length}
            report={drillStack[drillStack.length - 1].report}
            tenantId={tenantId}
            siteId={siteId}
            runtimeFilters={drillStack[drillStack.length - 1].runtimeFilters}
            onRowClick={handleRowClick}
            timezone={suiteDetail?.timezone}
            hidePanelHeader
            showApiAction={false}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDrillStack([])}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading && !suiteDetail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMsg) {
    return <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>;
  }

  // ── Level-2: stacked sibling tables (suite `level2ReportNames`) ────────────
  // Per-user drill takes precedence so a row click from an env users table
  // can open the user page while keeping l2View for Back.
  if (l2View && !userDrill) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => setL2View(null)}
          sx={{ mb: 2, ...reportsDrilldownBackButtonSx(theme) }}
        >
          Back
        </Button>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: { xs: '55vh', md: 'calc(100vh - 200px)' },
        }}>
          {l2View.reports.map((report, idx) => (
            <ReportPanel
              key={report._id || report.name}
              report={report}
              tenantId={tenantId}
              siteId={siteId}
              timeRangeOverride={timeRangeOverride}
              runtimeFilters={l2View.runtimeFilters}
              contextBadge={idx === 0 ? l2View.badge : undefined}
              onRowClick={handleRowClick}
              timezone={suiteDetail?.timezone}
              hidePanelHeader={idx === 0 ? !l2View.badge : false}
              showApiAction={false}
            />
          ))}
        </Box>
        {detailDialogNode}
      </Box>
    );
  }

  // ── Level-2: per-user drill-down ───────────────────────────────────────────
  if (userDrill) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => setUserDrill(null)}
          sx={{ mb: 2, ...reportsDrilldownBackButtonSx(theme) }}
        >
          Back
        </Button>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
          {`user_id = ${userDrill.userId}`}
        </Typography>
        {userDrilldownReports.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            This suite has no per-user drill-down reports.
          </Typography>
        ) : (
          <Box sx={{
            display: 'grid', gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
          }}>
            {userDrilldownReports.map((r) => (
              <Box key={r._id} sx={{ gridColumn: { md: `span ${clampSpan(r.layout?.w)}` }, minWidth: 0, width: '100%' }}>
                <ReportPanel
                  report={r}
                  tenantId={tenantId}
                  siteId={siteId}
                  timeRangeOverride={timeRangeOverride}
                  runtimeFilters={userRuntimeFilters}
                  contextBadge={`user_id = ${userDrill.userId}`}
                  timezone={suiteDetail?.timezone}
                  showApiAction={false}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // ── Level-1: panel drill-down ──────────────────────────────────────────────
  if (level1Report) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => setLevel1ReportId(null)}
          sx={{ mb: 2, ...reportsDrilldownBackButtonSx(theme) }}
        >
          Back
        </Button>
        {level1LinkedReports.length > 1 ? (
          <Box sx={{
            display: 'grid', gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
          }}>
            {level1LinkedReports.map((report) => (
              <Box
                key={report._id || report.name}
                sx={{ gridColumn: { md: `span ${clampSpan(report.layout?.w ?? 4)}` }, minWidth: 0, width: '100%' }}
              >
                <ReportPanel
                  report={report}
                  tenantId={tenantId}
                  siteId={siteId}
                  timeRangeOverride={timeRangeOverride}
                  onRowClick={handleRowClick}
                  onPanelClick={handlePanelClick}
                  timezone={suiteDetail?.timezone}
                  hidePanelHeader={false}
                  showApiAction={false}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: '55vh', md: 'calc(100vh - 200px)' } }}>
            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <ReportPanel
                report={level1Report}
                tenantId={tenantId}
                siteId={siteId}
                timeRangeOverride={timeRangeOverride}
                onRowClick={handleRowClick}
                timezone={suiteDetail?.timezone}
                hidePanelHeader
                showApiAction={false}
              />
            </Box>
          </Box>
        )}
        {detailDialogNode}
      </Box>
    );
  }

  // ── Default: full suite dashboard ──────────────────────────────────────────
  const hasAnyPanel = dashboardReports.length > 0 || realtimeReports.length > 0 || toolbarReports.length > 0;

  return (
    <Box>
      {/* Counter/toolbar tiles strip. */}
      {toolbarReports.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {toolbarReports.map((r) => (
            <CounterTile
              key={r._id}
              report={r}
              tenantId={tenantId}
              siteId={siteId}
              timeRangeOverride={timeRangeOverride}
              timezone={suiteDetail?.timezone}
            />
          ))}
        </Box>
      )}

      {/* Realtime panels, full width, above the dashboard grid. */}
      {realtimeReports.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {realtimeReports.map((r) => (
            <RealtimePanel
              key={r._id}
              report={r}
              tenantId={tenantId}
              siteId={siteId}
              timezone={suiteDetail?.timezone}
              showApiAction={false}
            />
          ))}
        </Box>
      )}

      {/* Standard dashboard grid. */}
      {dashboardReports.length > 0 && (
        <Box sx={{
          display: 'grid', gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
        }}>
          {dashboardReports.map((report) => (
            <Box
              key={report._id}
              sx={{ gridColumn: { md: `span ${clampSpan(report.layout?.w)}` }, minWidth: 0, width: '100%' }}
            >
              <ReportPanel
                report={report}
                tenantId={tenantId}
                siteId={siteId}
                timeRangeOverride={timeRangeOverride}
                onRowClick={handleRowClick}
                onPanelClick={handlePanelClick}
                timezone={suiteDetail?.timezone}
                showApiAction={false}
              />
            </Box>
          ))}
        </Box>
      )}

      {!loading && suiteDetail && !hasAnyPanel && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            This suite has no reports yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add reports to this suite to display them here.
          </Typography>
        </Box>
      )}

      <Box sx={{ height: theme.spacing(4) }} aria-hidden />
      {detailDialogNode}
    </Box>
  );
};

export default SuiteReportsView;
